import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BsChevronDown, BsChevronUp, BsInfoCircle } from 'react-icons/bs';
import ToolTip from 'src/components/Tooltip/Tooltip';
import _ from 'lodash';
import type { CatalogOption, CustomizerTab, OptionId } from '../domain';
import { useConfiguratorState } from '../store';
import { useConfiguratorFeatures } from '../features';
import { useShowIncompatible } from '../showIncompatibleStore';
import { useVehicle } from '../api';
import {
	availabilityOf,
	formatCurrency,
	partitionByAvailability,
	type Availability,
} from '../common';
import styles from './CustomizerOptions.module.scss';

const useAvailability = (optionId: OptionId): Availability => {
	const committedPicks = useConfiguratorState((s) => s.committedPicks);
	const domains = useConfiguratorState((s) => s.domains);
	return availabilityOf(optionId, committedPicks, domains);
};

const useCatalog = () => {
	const vehicle = useConfiguratorState((s) => s.vehicle);
	return useVehicle(vehicle).data?.catalog ?? null;
};

const useDrivers = (optionId: OptionId): string[] => {
	const forcedBy = useConfiguratorState((s) => s.forcedBy);
	const catalog = useCatalog();
	if (!catalog) return [];

	return (forcedBy.get(optionId) ?? [])
		.map((id) => catalog.byId.get(id))
		.filter((o): o is CatalogOption => !!o)
		.map((o) => (o.code ? `${o.name} [Cod. ${o.code}]` : o.name));
};

const useBlockers = (optionId: OptionId): string[] => {
	const blockedBy = useConfiguratorState((s) => s.blockedBy);
	const catalog = useCatalog();
	if (!catalog) return [];

	return (blockedBy.get(optionId) ?? [])
		.map((id) => catalog.byId.get(id))
		.filter((o): o is CatalogOption => !!o)
		.map((o) => (o.code ? `${o.name} [Cod. ${o.code}]` : o.name));
};

const useRowHint = (
	availability: Availability,
	drivers: string[],
	blockers: string[]
): string | undefined => {
	const { t } = useTranslation();

	if (availability === 'locked') {
		if (drivers.length > 0) {
			return `${t('car_configurator_customizer_required_by', {
				defaultValue: 'Richiesto da',
			})} ${drivers.join(', ')}.`;
		}
		return t('car_configurator_customizer_included_hint', {
			defaultValue:
				'Incluso dalla tua configurazione. Per cambiarlo, modifica la selezione che lo richiede.',
		});
	}

	if (availability === 'blocked') {
		if (blockers.length > 0) {
			return `${t('car_configurator_customizer_blocked_by', {
				defaultValue: 'Non compatibile con',
			})} ${blockers.join(', ')}.`;
		}
		return t('car_configurator_customizer_blocked_hint', {
			defaultValue: 'Non disponibile con la selezione attuale.',
		});
	}

	return undefined;
};

function RowInfo({
	optionId,
	label,
	children,
}: {
	optionId: OptionId;
	label: string;
	children: React.ReactNode;
}) {
	const anchorId = `mn-row-info-${optionId}`;
	return (
		<>
			<span
				id={anchorId}
				className={styles.infoIcon}
				role="img"
				aria-label={label}
			>
				<BsInfoCircle aria-hidden />
			</span>
			<ToolTip anchorId={anchorId} place="top" clickable>
				{children}
			</ToolTip>
		</>
	);
}

const priceOf = (o: CatalogOption): number => o.basePrice;

const priceLabel = (price: number): string =>
	price === 0 ? '€ 0' : `+ ${formatCurrency(price)}`;

function PriceCell({ option }: { option: CatalogOption }) {
	const { t } = useTranslation();
	// codiceDisponibilita 'G' = gratuito
	const label =
		option.availability === 'G' && priceOf(option) <= 0
			? t('car_configurator_customizer_free', { defaultValue: 'Gratuito' })
			: priceLabel(priceOf(option));
	return <div className={styles.priceCol}>{label}</div>;
}

function RowLabel({
	option,
	hint,
}: {
	option: CatalogOption;
	hint: string | undefined;
}) {
	const { t } = useTranslation();
	const showTwinInfo = useConfiguratorFeatures((f) => f.showTwinInfo);

	// Per i pacchetti la `descrizioneBreve` è l'elenco (lunghissimo) degli optional
	// inclusi: è già mostrato nella sezione espandibile "Mostra contenuto", quindi
	// non lo ripetiamo nel tooltip. L'hint di compatibilità invece resta.
	const base = hint ?? (option.isPack ? undefined : option.tooltip) ?? undefined;

	// Vernici collassate: aggiungiamo al tooltip il codice (e il nome, se distinto)
	// del gemello tecnico, così l'utente sa quali due codici porta la vernice.
	const twinNote =
		showTwinInfo && option.twinCode
			? t('car_configurator_customizer_twin_hint', {
					defaultValue: 'Abbinata al codice gemello {{code}}.',
					code: option.twinName
						? `${option.twinCode} (${option.twinName})`
						: option.twinCode,
			  })
			: undefined;

	const hasTooltip = Boolean(base || twinNote);
	// aria-label: le due parti su una riga sola, il resto è nel corpo JSX.
	const ariaLabel = [base, twinNote].filter(Boolean).join(' · ');

	return (
		<div className={styles.content}>
			<div className={styles.nameRow}>
				<span className={styles.name}>
					{option.name}{' '}
					{option.code && (
						<span className={styles.code}>[Cod. {option.code}]</span>
					)}
				</span>
				{hasTooltip && (
					<RowInfo optionId={option.id} label={ariaLabel}>
						{base && <div>{base}</div>}
						{twinNote && (
							<ul className={styles.tooltipList}>
								<li>{twinNote}</li>
							</ul>
						)}
					</RowInfo>
				)}
			</div>
			{option.subtitle && <div className={styles.code}>{option.subtitle}</div>}
		</div>
	);
}

interface RowProps {
	option: CatalogOption;
}

export function OptionRow({ option }: RowProps) {
	const availability = useAvailability(option.id);
	const toggle = useConfiguratorState((s) => s.toggleOption);

	const drivers = useDrivers(option.id);
	const blockers = useBlockers(option.id);
	const hint = useRowHint(availability, drivers, blockers);

	const selected = availability === 'selected';
	const locked = availability === 'locked';
	const blocked = availability === 'blocked';

	const rowClass = [
		styles.row,
		selected ? styles.selected : '',
		locked ? styles.locked : '',
		locked ? styles.disabled : '',
		blocked ? styles.conflicting : '',
	]
		.filter(Boolean)
		.join(' ');

	return (
		<div
			className={rowClass}
			onClick={() => !locked && toggle(option.id)}
			role="checkbox"
			aria-checked={selected || locked}
			aria-disabled={locked}
			aria-label={option.name}
		>
			<div className={styles.checkbox}>{selected || locked ? '✓' : ''}</div>
			<RowLabel option={option} hint={hint} />
			<PriceCell option={option} />
		</div>
	);
}

const usePackContents = (packId: OptionId): string[] => {
	const catalog = useCatalog();
	return useMemo(() => {
		if (!catalog) return [];
		return (catalog.packMembers.get(packId) ?? [])
			.map((id) => catalog.byId.get(id))
			.filter((o): o is CatalogOption => !!o)
			.map((o) => o.name || o.subtitle || o.code)
			.filter(Boolean);
	}, [catalog, packId]);
};

export function PackRow({ option }: RowProps) {
	const availability = useAvailability(option.id);
	const toggle = useConfiguratorState((s) => s.toggleOption);
	const [expanded, setExpanded] = useState(false);
	const { t } = useTranslation();

	const includedNames = usePackContents(option.id);
	const drivers = useDrivers(option.id);
	const blockers = useBlockers(option.id);
	const hint = useRowHint(availability, drivers, blockers);

	const selected = availability === 'selected';
	const locked = availability === 'locked';
	const blocked = availability === 'blocked';
	const frozen = locked || blocked;

	const rowClass = [
		styles.packRow,
		selected ? styles.selected : '',
		locked ? styles.locked : '',
		frozen ? styles.disabled : '',
	]
		.filter(Boolean)
		.join(' ');

	return (
		<div className={rowClass}>
			<div
				className={styles.packMain}
				onClick={() => !frozen && toggle(option.id)}
				role="checkbox"
				aria-checked={selected || locked}
				aria-disabled={frozen}
				aria-label={option.name}
			>
				<div className={styles.checkbox}>{selected || locked ? '✓' : ''}</div>
				<RowLabel option={option} hint={hint} />
				<PriceCell option={option} />
			</div>

			{includedNames.length > 0 && (
				<>
					<button
						className={styles.expandBtn}
						onClick={() => setExpanded((e) => !e)}
					>
						<span className={styles.expandBtnLabel}>
							{expanded
								? t('car_configurator_customizer_hide_contents', {
										defaultValue: 'Nascondi contenuto',
								  })
								: t('car_configurator_customizer_show_contents', {
										defaultValue: 'Mostra contenuto',
								  })}
							{expanded ? <BsChevronUp /> : <BsChevronDown />}
						</span>
					</button>
					{expanded && (
						<div className={styles.includesBody}>
							<ul className={styles.includesList}>
								{includedNames.map((name, i) => (
									<li key={`${option.id}-member-${i}`}>{name}</li>
								))}
							</ul>
						</div>
					)}
				</>
			)}
		</div>
	);
}

type CategoryBucket = { key: string; name: string; options: CatalogOption[] };

const groupByCategory = (opts: CatalogOption[]): CategoryBucket[] => {
	const buckets: CategoryBucket[] = [];
	const byKey = new Map<string, CategoryBucket>();

	for (const o of opts) {
		const key = o.categoryKey || o.category || 'Altro';
		let bucket = byKey.get(key);
		if (!bucket) {
			bucket = {
				key,
				name: o.category || o.categoryKey || 'Altro',
				options: [],
			};
			byKey.set(key, bucket);
			buckets.push(bucket);
		}
		bucket.options.push(o);
	}

	return buckets;
};

interface OptionGroupListProps {
	tab: CustomizerTab;
	options: CatalogOption[];
	searchQuery: string;
	emptyMessage: string;
}

export function OptionGroupList({
	tab,
	options,
	searchQuery,
	emptyMessage,
}: OptionGroupListProps) {
	const showTwinInSearch = useConfiguratorFeatures((f) => f.showTwinInSearch);
	const truncate = useConfiguratorFeatures((f) => f.optionsPreviewCount);
	// toggle unico globale (persistito): true = mostra i non compatibili in linea
	const showIncompatible = useShowIncompatible((s) => s.show);
	const { t } = useTranslation();
	const [expandedCat, setExpandedCat] = useState<Record<string, boolean>>({});
	const selectedOptionIds = useConfiguratorState((s) => s.selectedOptionIds);
	const committedPicks = useConfiguratorState((s) => s.committedPicks);
	const domains = useConfiguratorState((s) => s.domains);

	const activeIds = useMemo(
		() => new Set(selectedOptionIds),
		[selectedOptionIds]
	);

	const grouped = useMemo(() => {
		const q = searchQuery.toLowerCase().trim();
		const filtered = options.filter(
			(o) =>
				!q ||
				o.name.toLowerCase().includes(q) ||
				o.subtitle.toLowerCase().includes(q) ||
				o.code.toLowerCase().includes(q) ||
				String(o.id).includes(q) ||
				// Vernici collassate: il gemello non è una voce a sé, ma il suo
				// codice/nome identifica la stessa vernice → matcha il master.
				// (Solo codice/nome: il twinId è l'idAccessorio interno, non un
				// codice digitabile, e il match per sottostringa darebbe falsi
				// positivi — es. "268" dentro "38650268".)
				(showTwinInSearch &&
					((o.twinName?.toLowerCase().includes(q) ?? false) ||
						(o.twinCode?.toLowerCase().includes(q) ?? false)))
		);
		return groupByCategory(filtered);
	}, [options, searchQuery, showTwinInSearch]);

	if (grouped.length === 0) {
		return <div className={styles.emptyMsg}>{emptyMessage}</div>;
	}

	const isColorTab = tab === 'colors';
	const showGroupHeaders = grouped.length > 1;
	// in ricerca esplicita non nascondiamo nulla: l'utente sta cercando
	const searching = searchQuery.trim().length > 0;

	return (
		<>
			{grouped.map(({ key, name, options: rawList }) => {
				const list = isColorTab ? _.sortBy(rawList, priceOf) : rawList;

				// nascondi gli optional non compatibili (blocked) salvo che il toggle
				// globale sia attivo o si stia cercando; il gruppo si "pulisce" man
				// mano che l'utente sceglie.
				const { visible: shown } =
					searching || showIncompatible
						? { visible: list }
						: partitionByAvailability(list, committedPicks, domains);

				const hasHiddenActive = shown.some(
					(o, i) => i >= truncate && activeIds.has(o.id)
				);
				const isExpanded = isColorTab || !!expandedCat[key] || hasHiddenActive;
				const rows = isExpanded ? shown : shown.slice(0, truncate);

				return (
					<div key={key} className="mb-4">
						{showGroupHeaders && (
							<div className={styles.groupHeader}>
								<div className={styles.groupHeaderLeft}>
									<h2 className={styles.groupTitle}>{name}</h2>
								</div>
								{list.length > 1 && (
									<span className={styles.groupCount}>
										{t('car_configurator_customizer_n_options', {
											defaultValue: '{{n}} optionals',
											n: list.length,
										})}
									</span>
								)}
							</div>
						)}

						{rows.map((o) =>
							o.isPack ? (
								<PackRow key={o.id} option={o} />
							) : (
								<OptionRow key={o.id} option={o} />
							)
						)}

						{!isExpanded && shown.length > truncate && (
							<button
								className={styles.vediTutto}
								onClick={() => setExpandedCat((e) => ({ ...e, [key]: true }))}
							>
								{t('car_configurator_customizer_show_all_options', {
									defaultValue: 'Visualizza tutti i {{n}} optional',
									n: shown.length,
								})}
							</button>
						)}
					</div>
				);
			})}
		</>
	);
}
